import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import get from 'lodash/get';
import NamedBox from '../atoms/NamedBox';
import Ranks, { RanksType } from '../atoms/Ranks';
import Loader from '../atoms/Loader';
import Accordion from '../atoms/Accordion';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { COLOR_BACKGROUND_LIGHT, COLOR_BORDER, COLOR_WHITE } from '../styles/colors';
import { inchesToFeetAndInches, secondsToMinutesSecondsMs } from '../services/converter';
import { FormatType, getEventFormatType, getEventName } from '../services/event-names-and-formats';
import Position from '../types/Position';
import Player from '../types/Player';
import Team from '../types/Team';
import PlayerCombineEvent from '../types/PlayerCombineEvent';
import PlayerCombineStats from '../types/PlayerCombineStats';
import PlayerNFLCombineEvent from '../types/PlayerNFLCombineEvent';
import PlayerNFLCombineStats from '../types/PlayerNFLCombineStats';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import Column from '../types/Column';
import {
  DEFAULT_PROFILE_STATS_COLUMNS,
  getCollegeHeaderItemByColumn,
  getNflHeaderItemByColumn,
  getProfileStatsComparisonRankValue
} from '../services/profile-stats-categories';
import { State } from '../redux/reducers';
interface PlayerProCombineDataProps {
  className?: string;
  loading?: boolean;
  player?: Player;
  collegeTeam?: Team;
  nflTeam?: Team;
  position?: Position;
  nflCombineEvents: Array<PlayerCombineEvent | PlayerNFLCombineEvent>;
  columns?: Column[];
  hideHeader?: boolean;
  isNFL?: boolean;
}

const MIN_EVENT_YEAR = 2010;

const useStyles = makeStyles((theme:Theme) => ({
  playerProCombineData: {
    borderBottom: 0,
    position: 'relative',
  },

  header: {
    backgroundColor: COLOR_WHITE,
    fontWeight: 700,
  },
  headerName: {
    fontWeight: 700,
  },

  event: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  eventLogo: {
    width: 64,
    height: 64,
  },
  eventInfo: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 78,
    borderRadius: 10,
    backgroundColor: COLOR_BACKGROUND_LIGHT,
    fontFamily: FONT_PROXIMA_NOVA,

    '@media (max-width: 1260px)': {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(1),
      minHeight: 0,
      borderRadius: 0,
      backgroundColor: 'transparent',
    },

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  eventInfoRow: {
    flexGrow: 1,
    flexBasis: '15%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    textAlign: 'center',
    borderRight: `1px solid ${COLOR_BORDER}`,

    '&:last-child': {
      borderRight: 0,
    },

    '@media (max-width: 1260px)': {
      border: 0,
      borderRadius: 10,
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },
  },
  eventInfoName: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
  },
  eventInfoValue: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1,
    fontWeight: 700,
  },
  hideHeader: {
    display: 'none',
  }
}), { name: PlayerProCombineData.name });

export default function PlayerProCombineData (props:PlayerProCombineDataProps) {
  const {
    className,
    loading = false,
    player,
    collegeTeam,
    nflTeam,
    position,
    nflCombineEvents,
    columns = [],
    hideHeader = false,
    isNFL,
  } = props;
  const classes = useStyles();
  const newProfileEnabled = useSelector((state: State) => Boolean(state.user?.program?.newProfileEnabled));

  const college = (player?.playerColleges || [])
    .find((college:College) => college && college.isPrimary);
  const highSchool = (player?.highSchools || [])
    .find((highSchool:HighSchool) => !!highSchool?.isPrimary);
  const recruitingClass = college?.recruitingClass || highSchool?.graduatingClass;

  function displayPerformance (performance:number, formatType:FormatType):string {
    if (formatType === FormatType.FEET_AND_INCHES) {
      return inchesToFeetAndInches(performance);
    } else if (formatType === FormatType.INCHES) {
      return `${Number(performance).toFixed(2)}"`;
    } else if (formatType === FormatType.MINUTES_SECONDS_MS) {
      return secondsToMinutesSecondsMs(performance);
    }

    return Number(performance).toFixed(2);
  }

  function getRanksEvent (stats:PlayerCombineStats | PlayerNFLCombineStats) {
    return {
      type: getEventName(stats.event),
      name: stats.meet ? `${stats.meet} ${stats.year ? `(${stats.year})` : ''}` : '',
      result: displayPerformance(stats.performance, getEventFormatType(stats.event)),
    };
  }

  function getEventYear (eventDate:string):number {
    return new Date(eventDate).getUTCFullYear();
  }

  function renderNFLEvents (
    stats:PlayerNFLCombineStats,
    event:PlayerCombineEvent | PlayerNFLCombineEvent,
    index:number,
  ) {
    return (
      <Ranks
        key={index}
        type={RanksType.PRO_COMBINE}
        playerId={player?.id}
        teamId={nflTeam?.id}
        collegeTeamId={collegeTeam?.id}
        nflTeamId={nflTeam?.id}
        position={position}
        comparisonEvent={stats.event}
        combineId={event.id}
        combineCategory={event.category}
        event={getRanksEvent(stats)}
        isNFL={isNFL}
        values={selectedColumns.map((column:Column, columnIndex:number) => {
          const comparisonStats = get(stats, 'comparisonStats', []);
          return getProfileStatsComparisonRankValue({
            column,
            columnIndex,
            comparisonStats,
            isNFL,
            newProfileEnabled,
          });
        })}
        bottomSpace
      />
    );
  }

  function renderCollegeEvents (
    stats:PlayerCombineStats,
    event:PlayerCombineEvent | PlayerNFLCombineEvent,
    index:number,
  ) {
    return (
      <Ranks
        key={index}
        type={RanksType.COMBINE}
        playerId={player && player.id}
        teamId={collegeTeam && collegeTeam.id}
        collegeTeamId={collegeTeam?.id}
        nflTeamId={nflTeam?.id}
        position={position}
        comparisonEvent={stats.event}
        combineId={event.id}
        combineCategory={event.category}
        isNFL={isNFL}
        event={getRanksEvent(stats)}
        values={selectedColumns.map((column:Column, columnIndex:number) => {
          const comparisonStats = get(stats, 'comparisonStats', []);
          return getProfileStatsComparisonRankValue({
            column,
            columnIndex,
            comparisonStats,
            isNFL,
            newProfileEnabled,
          });
        })}
        bottomSpace
      />
    );
  }

  const selectedColumns = (columns.filter((column:Column) => column.selected).length
    ? columns.filter((column:Column) => column.selected)
    : DEFAULT_PROFILE_STATS_COLUMNS) as Column[];

  function renderEvent(event:PlayerCombineEvent | PlayerNFLCombineEvent) {
    const eventYear = event?.combineDate ? new Date(event.combineDate).getUTCFullYear() : 0;

    return (
      <div className={classes.event}>
        <div className={classes.eventInfo}>
          <div className={classes.eventInfoRow}>
            <div className={classes.eventInfoName}>Event</div>
            <div className={classes.eventInfoValue}>{event.type}</div>
          </div>

          {!!event.city && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>City</div>
              <div className={classes.eventInfoValue}>{event.city}</div>
            </div>
          )}

          {eventYear >= MIN_EVENT_YEAR && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Year</div>
              <div className={classes.eventInfoValue}>{getEventYear(event.combineDate)}</div>
            </div>
          )}

          {!!event.height && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Height</div>
              <div className={classes.eventInfoValue}>{inchesToFeetAndInches(event.height, true)}</div>
            </div>
          )}

          {!!event.weight && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Weight</div>
              <div className={classes.eventInfoValue}>{event.weight} lbs.</div>
            </div>
          )}
        </div>

        {/* <table className={classes.eventInfo}>
          <tbody>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Hand</td>
              <td className={classes.eventInfoValue}>{9.125}</td>
            </tr>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Arm</td>
              <td className={classes.eventInfoValue}>{30.625}</td>
            </tr>
            <tr className={classes.eventInfoRow}>
              <td className={classes.eventInfoName}>Wing</td>
              <td className={classes.eventInfoValue}>{75.875}</td>
            </tr>
          </tbody>
        </table> */}
      </div>
    );
  }

  return (
    <NamedBox
      className={clsx(classes.playerProCombineData, className)}
      headerClassName={clsx(classes.header, hideHeader && classes.hideHeader)}
      headerNameClassName={classes.headerName}
      name={`Pro Combine Data - ${position}`}
    >
      <Loader inProgress={loading} />

      {!!nflCombineEvents?.length && (
        nflCombineEvents?.map((event:PlayerCombineEvent | PlayerNFLCombineEvent, index:number) => (
          <Accordion
            key={index}
            summary={event.type}
            expanded={index === 0 || index === 1}
          >
            {renderEvent(event)}

            {!isNFL && (
              <Ranks
                headerItems={selectedColumns.map((column:Column) => getCollegeHeaderItemByColumn(column, {
                  recruitingClass,
                  collegeTeam,
                }))}
              />
            )}

            {isNFL && (
              <Ranks
                headerItems={selectedColumns.map((column:Column) => getNflHeaderItemByColumn(column, {
                  collegeTeam,
                  nflTeam,
                }))}
              />
            )}

            {(event.eventStats && !!event.eventStats.length) && (
              (event.eventStats as any[])
                .filter((stats:any) => !!stats.performance)
                .map((stats:any, index:number) => 
                (
                  isNFL
                    ? renderNFLEvents(stats as PlayerNFLCombineStats, event, index)
                    : renderCollegeEvents(stats as PlayerCombineStats, event, index)
                  )
                )
            )}
          </Accordion>
        ))
      )}
    </NamedBox>
  );
}
